import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardPermissions } from '@/hooks/useDashboardPermissions';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { StatCard } from './StatCard';
import { FolderKanban, Building2, MessageSquare, Home, HardHat, CheckCircle, Clock, ClipboardList, Globe, Briefcase, Megaphone, DollarSign, BarChart3, Sparkles, ExternalLink, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SubscriptionStatusCard } from '@/components/dashboard/SubscriptionStatusCard';

export function DeveloperDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { permissions: perms } = useDashboardPermissions();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['developer-dashboard-stats', user?.id],
    queryFn: async () => {
      const { data: devProfile } = await supabase
        .from('developer_profiles')
        .select('id')
        .eq('user_id', user!.id)
        .single();

      if (!devProfile) return { totalProjects: 0, approvedProjects: 0, pendingProjects: 0, totalUnits: 0, availableUnits: 0, totalLeads: 0 };

      const { data: projects } = await supabase
        .from('projects')
        .select('id, total_units, available_units, approval_status, status')
        .eq('developer_id', devProfile.id);

      const totalProjects = projects?.length || 0;
      const approvedProjects = projects?.filter(p => p.approval_status === 'approved').length || 0;
      const pendingProjects = projects?.filter(p => p.approval_status === 'pending').length || 0;
      const totalUnits = projects?.reduce((sum, p) => sum + (p.total_units || 0), 0) || 0;
      const availableUnits = projects?.reduce((sum, p) => sum + (p.available_units || 0), 0) || 0;

      const projectIds = projects?.map(p => p.id) || [];
      let totalLeads = 0;
      if (projectIds.length > 0) {
        const { count } = await supabase
          .from('leads')
          .select('*', { count: 'exact', head: true })
          .in('project_id', projectIds);
        totalLeads = count || 0;
      }

      return { totalProjects, approvedProjects, pendingProjects, totalUnits, availableUnits, totalLeads };
    },
    enabled: !!user,
  });

  const { data: recentProjects } = useQuery({
    queryKey: ['developer-recent-projects', user?.id],
    queryFn: async () => {
      const { data: devProfile } = await supabase
        .from('developer_profiles')
        .select('id')
        .eq('user_id', user!.id)
        .single();
      if (!devProfile) return [];
      const { data } = await supabase
        .from('projects')
        .select('id, name, city, status, approval_status, total_units, available_units, price_min, price_max, currency')
        .eq('developer_id', devProfile.id)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: !!user,
  });

  // Developer profile (for public link)
  const { data: devProfileInfo } = useQuery({
    queryKey: ['developer-self-profile', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('developer_profiles')
        .select('id, slug')
        .eq('user_id', user!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  // Recent project leads
  const { data: recentLeads } = useQuery({
    queryKey: ['developer-recent-leads', devProfileInfo?.id],
    enabled: !!devProfileInfo?.id,
    queryFn: async () => {
      const { data: projects } = await supabase
        .from('projects')
        .select('id')
        .eq('developer_id', devProfileInfo!.id);
      const ids = (projects || []).map(p => p.id);
      if (!ids.length) return [];
      const { data } = await supabase
        .from('leads')
        .select('id, name, phone, status, created_at, project_id')
        .in('project_id', ids)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
  });

  const statusIcons: Record<string, any> = {
    upcoming: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-100' },
    under_construction: { icon: HardHat, color: 'text-orange-500', bg: 'bg-orange-100' },
    ready: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100' },
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  const quickActions = [
    { label: 'Add Project', url: '/dashboard/projects/new', icon: Plus, color: 'bg-primary/10 text-primary', show: perms.canAddProject },
    { label: 'My Projects', url: '/dashboard/my-projects', icon: FolderKanban, color: 'bg-indigo-500/10 text-indigo-600', show: perms.canViewMyProjects },
    { label: 'Bookings', url: '/dashboard/bookings', icon: ClipboardList, color: 'bg-blue-500/10 text-blue-600', show: perms.canManageBookings },
    { label: 'Plot Map', url: '/dashboard/plot-map', icon: Globe, color: 'bg-emerald-500/10 text-emerald-600', show: perms.canManageBookings },
    { label: 'Transfers', url: '/dashboard/transfers', icon: Briefcase, color: 'bg-amber-500/10 text-amber-600', show: perms.canManageBookings },
    { label: 'Inventory', url: '/dashboard/inventory', icon: HardHat, color: 'bg-orange-500/10 text-orange-600', show: perms.canManageBookings },
    { label: 'Project Leads', url: '/dashboard/leads', icon: MessageSquare, color: 'bg-primary/10 text-primary', show: perms.canViewLeads },
    { label: 'My Banner Ads', url: '/dashboard/my-banners', icon: Megaphone, color: 'bg-pink-500/10 text-pink-600', show: perms.canManageMyBanners },
    { label: 'My Invoices', url: '/dashboard/my-invoices', icon: DollarSign, color: 'bg-emerald-500/10 text-emerald-600', show: true },
    { label: 'Analytics', url: '/dashboard/analytics', icon: BarChart3, color: 'bg-blue-500/10 text-blue-600', show: perms.canViewAnalytics },
    { label: 'Buy Package', url: '/advertise?pkg=developer', icon: Sparkles, color: 'bg-amber-500/10 text-amber-600', show: true },
  ].filter(a => a.show);

  const leadStatusColors: Record<string, string> = {
    new: 'bg-primary', contacted: 'bg-amber-500', converted: 'bg-emerald-500', closed: 'bg-muted-foreground',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Developer Dashboard</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage your projects, track units, and monitor inquiries.</p>
        </div>
        {devProfileInfo?.slug && (
          <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate(`/developers/${devProfileInfo.slug}`)}>
            <ExternalLink className="h-4 w-4" /> View Public Profile
          </Button>
        )}
      </div>

      <SubscriptionStatusCard />


      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard value={stats?.totalProjects || 0} label="Total Projects" variant="sale" progress={Math.min((stats?.totalProjects || 0) * 20, 100)} />
        <StatCard value={stats?.totalUnits || 0} label="Total Units" variant="rent" progress={stats?.totalUnits ? Math.round(((stats?.availableUnits || 0) / stats.totalUnits) * 100) : 0} />
        <StatCard value={stats?.availableUnits || 0} label="Available Units" variant="customer" progress={stats?.totalUnits ? Math.round(((stats?.availableUnits || 0) / stats.totalUnits) * 100) : 0} />
        <StatCard value={stats?.totalLeads || 0} label="Project Inquiries" variant="city" progress={Math.min((stats?.totalLeads || 0) * 5, 100)} />
      </div>

      {/* Project Status Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats?.approvedProjects || 0}</div>
              <div className="text-xs text-muted-foreground">Approved Projects</div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-yellow-100 flex items-center justify-center">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats?.pendingProjects || 0}</div>
              <div className="text-xs text-muted-foreground">Pending Approval</div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold">{(stats?.totalUnits || 0) - (stats?.availableUnits || 0)}</div>
              <div className="text-xs text-muted-foreground">Units Sold/Reserved</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" /> Quick Actions
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {quickActions.map(item => (
              <button
                key={item.label}
                onClick={() => navigate(item.url)}
                className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors text-left"
              >
                <div className={`h-10 w-10 rounded-lg ${item.color} flex items-center justify-center shrink-0`}>
                  <item.icon className="h-5 w-5" />
                </div>
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Project Leads */}
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-primary" /> Recent Project Leads
            </h3>
            <button onClick={() => navigate('/dashboard/leads')} className="text-xs text-primary font-semibold hover:underline">View All</button>
          </div>
          {recentLeads && recentLeads.length > 0 ? (
            <div className="space-y-3">
              {recentLeads.map((lead: any) => (
                <div key={lead.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                  <div className={`h-2.5 w-2.5 rounded-full ${leadStatusColors[lead.status] || 'bg-muted-foreground'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{lead.name}</div>
                    <div className="text-xs text-muted-foreground">{lead.phone}</div>
                  </div>
                  <div className="text-[10px] text-muted-foreground">{new Date(lead.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">No project inquiries yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Projects */}
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <FolderKanban className="h-4 w-4 text-primary" /> My Projects
            </h3>
            {perms.canAddProject && (
              <button onClick={() => navigate('/dashboard/projects/new')} className="text-xs text-primary font-semibold hover:underline">Add New Project</button>
            )}
          </div>
          {recentProjects && recentProjects.length > 0 ? (
            <div className="space-y-3">
              {recentProjects.map((proj: any) => {
                const si = statusIcons[proj.status] || statusIcons.upcoming;
                const StatusIcon = si.icon;
                return (
                  <div key={proj.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                    <div className={`h-10 w-10 rounded-lg ${si.bg} flex items-center justify-center shrink-0`}>
                      <StatusIcon className={`h-5 w-5 ${si.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {typeof proj.name === 'object' ? (proj.name as any)?.en || 'Project' : 'Project'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {proj.city} · {proj.total_units || 0} units · {proj.available_units || 0} available
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${proj.approval_status === 'approved' ? 'bg-green-100 text-green-700' : proj.approval_status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                        {proj.approval_status}
                      </span>
                      <div className="text-[10px] text-muted-foreground mt-1 capitalize">{proj.status?.replace('_', ' ')}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No projects yet. Create your first project to get started.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
